import { ActivityData, ClassroomStatus } from '../../../interfaces/activity';
import {
    ActivityLMSType,
    ActivityModality,
    ActivityProgram,
    ActivityStatus,
    ActivityType,
    ActivityViltType,
} from './Common';

export const MAX_CLASSROOM_CLASS_SIZE = 300;

// Explicit allowlist of activity types eligible for classroom creation.
export const SUPPORTED_ACTIVITY_TYPES = [
    ActivityType.Class,
    ActivityType.ClassBoltOnCloudQuest,
    ActivityType.TTT,
    ActivityType.Event,
] as const;

// Configuration constants
export const CLASSROOM_CONFIG = {
    POLLING_INTERVAL: 2000,
    MIN_CLASS_SIZE: 1,
    MAX_CLASS_SIZE: MAX_CLASSROOM_CLASS_SIZE,
    SUPPORTED_ACTIVITY_TYPES,
    SUPPORTED_PROGRAMS: [
        ActivityProgram.Commercial,
        ActivityProgram.Partner,
        ActivityProgram.SkillsCenters,
        ActivityProgram.WorldWideFieldEnablement,
        ActivityProgram.PartnerReseller,
        ActivityProgram.AAI,
    ],
    // disable classroom creation button for UNSUPPORTED_COURSES
    UNSUPPORTED_COURSES: [],
    LMS_REQUIREMENTS: {
        [ActivityLMSType.Kiku]: ['vIltId'],
        [ActivityLMSType.CustomerLMS]: ['vIltId', 'vIltMeetingId'],
        [ActivityLMSType.MyClass]: ['vIltId'],
        [ActivityLMSType.SkillBuilder]: ['vIltId'],
    },
    TERMINAL_STATUSES: [
        ClassroomStatus.CLASSROOM_UPDATE_FAILED,
        ClassroomStatus.CANCELLED,
        ClassroomStatus.CLASSROOM_PROVISIONING_FAILED,
        ClassroomStatus.CLASSROOM_READY,
        ClassroomStatus.READY_FOR_CLASSROOM_CREATION,
    ],
} as const;

// Basic validation functions
export const isActivityTypeSupported = (type: string): boolean =>
    SUPPORTED_ACTIVITY_TYPES.some((supportedType) => supportedType === type);

export const isProgramSupported = (
    program: string,
    options?: { isMyClassAdcEnabled?: boolean },
): boolean =>
    CLASSROOM_CONFIG.SUPPORTED_PROGRAMS.some(
        (supportedProgram) => supportedProgram === program,
    ) ||
    (!!options?.isMyClassAdcEnabled &&
        program === ActivityProgram.DedicatedCloud);

export const hasRequiredInstructors = (instructors?: any[]): boolean =>
    Boolean(instructors && instructors.length > 0);

export const hasValidClassSize = (size?: number): boolean =>
    Boolean(
        size &&
        size >= CLASSROOM_CONFIG.MIN_CLASS_SIZE &&
        size <= CLASSROOM_CONFIG.MAX_CLASS_SIZE,
    );

export const isActivityActive = (status: string): boolean =>
    status === ActivityStatus.Active;

// LMS-specific validation
export const validateLMSRequirements = (activity: ActivityData): boolean => {
    const { activity_modality, lms_type, v_ilt_id, v_ilt_meeting_id, program } =
        activity;

    // Bypass LMS requirements for ILT modality
    if (activity_modality === ActivityModality.ILT) {
        return true;
    }

    // Bypass vILT link requirements for "Other" vILT type with MyClass LMS.
    // "Other" means the meeting is managed outside Grimsby (e.g., Zoom, Teams)
    // and the vILT link may not be known at scheduling time.
    if (
        activity_modality === ActivityModality.vILT &&
        activity.v_ilt_type === ActivityViltType.Other &&
        lms_type === ActivityLMSType.MyClass
    ) {
        return true;
    }

    // Bypass LMS requirements for Dedicated Cloud program or None LMS type
    if (
        program === ActivityProgram.DedicatedCloud ||
        lms_type === ActivityLMSType.None
    ) {
        return true;
    }

    // Check LMS-specific requirements
    const requirements =
        CLASSROOM_CONFIG.LMS_REQUIREMENTS[lms_type as ActivityLMSType];
    if (!requirements) {
        return false;
    }

    const hasVIltId = Boolean(v_ilt_id);
    const hasVIltMeetingId = Boolean(v_ilt_meeting_id);

    return requirements.every((req) => {
        if (req === 'vIltId') {
            return hasVIltId;
        }
        if (req === 'vIltMeetingId') {
            return hasVIltMeetingId;
        }
        return false;
    });
};

export const isSupportedCourse = (courseName: string): boolean =>
    !(CLASSROOM_CONFIG.UNSUPPORTED_COURSES as readonly string[]).includes(
        courseName,
    );

/**
 * Checks whether an activity will create a managed classroom, based on
 * stable/structural attributes (program, activity type). Does not consider
 * transient state like status, instructors, or LMS link fields, so the
 * result is stable across the activity's lifecycle (create -> hold ->
 * active -> completed).
 *
 * Use this to decide whether classroom-specific policy limits (such as the
 * 300-seat class size cap) should apply to an activity.
 */
export const activityNeedsClassroom = (
    activity: Pick<ActivityData, 'program' | 'activity_type'>,
    options?: { isMyClassAdcEnabled?: boolean },
): boolean =>
    isProgramSupported(activity.program, options) &&
    isActivityTypeSupported(activity.activity_type);

/**
 * Validates the class size cap for activities that create a managed
 * classroom. Returns an error message if the activity needs a classroom
 * and its class size exceeds the cap, otherwise null. Activities that do
 * not create managed classrooms are exempt from this cap.
 */
export const validateClassroomClassSizeCap = (
    activity: Pick<ActivityData, 'program' | 'activity_type' | 'class_size'>,
): string | null => {
    if (activity.class_size == null) return null;
    if (!activityNeedsClassroom(activity)) return null;
    return activity.class_size > MAX_CLASSROOM_CLASS_SIZE
        ? `Class size must be ${MAX_CLASSROOM_CLASS_SIZE} or less for activities that create a classroom.`
        : null;
};

/**
 * Validates the classroom class size cap against the sum of
 * number_of_students_committed across customers. Returns an error message
 * if the activity needs a classroom and the total commitments exceed the
 * cap, otherwise null.
 */
export const validateClassroomCommitmentsCap = (
    activity: Pick<ActivityData, 'program' | 'activity_type'>,
    customers: ReadonlyArray<{ number_of_students_committed?: number | null }>,
): string | null => {
    if (!activityNeedsClassroom(activity)) return null;
    const commitmentsSum = customers.reduce(
        (total, customer) =>
            total + (customer.number_of_students_committed || 0),
        0,
    );
    return commitmentsSum > MAX_CLASSROOM_CLASS_SIZE
        ? `Sum of students committed (${commitmentsSum}) must be ${MAX_CLASSROOM_CLASS_SIZE} or less for activities that create a classroom.`
        : null;
};

// Comprehensive validation with reasons
export interface EligibilityCheck {
    isEligible: boolean;
    reasons: string[];
    checks: {
        isActive: boolean;
        supportedProgram: boolean;
        hasInstructors: boolean;
        hasValidSize: boolean;
        lmsRequirementsMet: boolean;
        isSupportedCourse: boolean;
    };
}

export const validateClassroomEligibility = (
    activity: ActivityData,
    options?: { isMyClassAdcEnabled?: boolean },
): EligibilityCheck => {
    const checks = {
        isActive: isActivityActive(activity.activity_status),
        supportedProgram: isProgramSupported(activity.program, options),
        hasInstructors: hasRequiredInstructors(activity.instructors),
        hasValidSize: hasValidClassSize(activity.class_size),
        lmsRequirementsMet: validateLMSRequirements(activity),
        isSupportedCourse: isSupportedCourse(activity.course_name),
    };

    const reasons: string[] = [];
    if (!checks.isSupportedCourse) {
        reasons.push('Course is not supported for classroom creation');
    } else {
        if (!checks.isActive) reasons.push('Activity must be Active');
        if (!checks.supportedProgram)
            reasons.push('Program type not supported for classrooms');
        if (!checks.hasInstructors)
            reasons.push('At least one instructor must be assigned');
        if (!checks.hasValidSize)
            reasons.push('Class size must be between 1 and 300');
        if (!checks.lmsRequirementsMet)
            reasons.push('LMS requirements not met for virtual delivery');
    }

    return {
        isEligible: Object.values(checks).every(Boolean),
        reasons,
        checks,
    };
};

// Status-related functions
export const hasExistingClassroom = (status?: string): boolean =>
    Boolean(
        status &&
        CLASSROOM_CONFIG.TERMINAL_STATUSES.some(
            (terminalStatus) => terminalStatus === status,
        ),
    );

export const isClassroomCreating = (status?: string): boolean =>
    status === ClassroomStatus.READY_FOR_CLASSROOM_CREATION;
